import { wallOpeningOpening3, wallOpeningOffset3, wallOpeningSideLayer } from "building-common/openings/openingQuery";
import {
  getSubMassParentTag,
  tagBuildingFace,
  tagBuildingFaces,
} from "building-types/buildingGeomTag/buildingGeomTags";
import type { BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import { TAG_CONDITION_HOLE } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { WallCut } from "building-types/walls/wallCutTypes";
import type { WallFaces } from "building-types/walls/wallFacesTypes";
import type { SolidWallMass } from "building-types/walls/wallTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { aabb2Overlaps } from "geom/aabb2";
import { faceNew, type Face, faceReverse } from "geom/brep";
import { classifyContainmentPolyLine2PolyLine2, PolyLine2PolyLine2ContainmentClass } from "geom/containment";
import { DIST_EPSILON } from "geom/core";
import { extrudeLine2 } from "geom/extrude";
import { type Line2Segment } from "geom/line2";
import { planeCopyMoved, planeNormal, planeProjectPolyLine3 } from "geom/plane";
import { polygonIntersection } from "geom/polyclip/polyClip";
import {
  polyLine2AABB2,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgesCollinearToLine,
  polyLine2SetCCW,
  polyLine2SetCW,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { type PolyLine2Polygon } from "geom/polyLine2Types";
import { type PolyLine3 } from "geom/polyLine3";
import { vec3Scale } from "geom/vec3";
import { openingShouldUseExteriorCasing } from "../openings/anyOpeningQuery";
import { wallFacesNew } from "./wallFaces";

export function solidWallMakeOpeningHoleFinishFaces({
  wallSideFinishMass,
  wallOpening,
  wallOpeningFinishWrapDistance,
  wallOpeningIsFront,
}: {
  wallSideFinishMass: SolidWallMass;
  wallOpening: GeneratedWindowPlacement | GeneratedDoorPlacement | WallCut;
  wallOpeningFinishWrapDistance: number;
  wallOpeningIsFront: boolean;
}): WallFaces {
  const faces = wallFacesNew();

  const tag: BuildingGeomTag[] = [
    {
      type: "PARENT",
      parentId: wallSideFinishMass.id,
      parentType: "WALL_LAYER",
    },
    TAG_CONDITION_HOLE,
  ];

  const subMassTag = getSubMassParentTag(wallSideFinishMass.leftFace);
  if (subMassTag) {
    tag.push(subMassTag);
  }

  const { thickness: finishThickness, rightFace, id: layerId } = wallSideFinishMass;
  const finishDepth = finishThickness + wallOpeningFinishWrapDistance;
  if (finishDepth < DIST_EPSILON) {
    return faces;
  }

  const { opening } = wallOpening;
  if (!opening) {
    return faces;
  }

  const { hasWrapping = false } = opening;

  // any active wrapping modes override the usage of casing
  // clipping profiles
  const useCasingLoop = openingShouldUseExteriorCasing(wallOpening);

  const side = wallOpeningIsFront ? "FRONT" : "BACK";

  // if this opening has been unioned with other openings, then it will have
  // both a union set and a set of "parts" (PolyLine2) of the whole that belong
  // to this opening
  const layer = wallOpeningSideLayer(opening, side, layerId);
  if (!layer.lines) {
    return faces;
  }

  const [pnx, pny, pnz] = planeNormal(rightFace.plane);
  const [dx, dy, dz] = vec3Scale(-pnx, -pny, -pnz, finishDepth);

  // currently we do not support unioning of openings with thick finish wrapping
  if (!hasWrapping || finishThickness === 0) {
    for (const line of layer.lines.lines) {
      const numEdges = polyLine2EdgeCount(line);
      for (let i = 0; i < numEdges; i++) {
        const edge = polyLine2Edge(line, i);

        const [x1, y1, x2, y2] = edge;

        const face = extrudeLine2(layer.lines.plane, x1, y1, x2, y2, dx, dy, dz);
        if (!face) {
          continue;
        }
        faces.rightHoleFaces.push(tagBuildingFace(face, tag));
      }
    }
    return faces;
  }

  const hole = wallOpeningOpening3(opening, side, useCasingLoop);
  // const offset = hasWrapping && finishThickness > 0 ? wallOpeningOffset3(opening, side, finishThickness) : undefined;
  const offset = wallOpeningOffset3(opening, side, finishThickness);

  // NOTE(Andrew): this is a special case for openings with offset holes:
  // For all openings, if the bottom of the *offset* opening hole is higher
  // than the bottom of the host face, the lower edge of the hole must
  // *always* be extruded. This is required because the bottom face of the
  // offset hole is often *not* offset, which would cause the bottom edge of
  // the hole to be missed.
  const holeEdges = layer.lines.lines;

  faces.rightHoleFaces.push(
    ...tagBuildingFaces(extrudeHoleEdges(rightFace, finishDepth, hole, offset, holeEdges), tag),
  );

  return faces;
}

interface HoleIntersection {
  /**
   * used to create the back face
   */
  intersection: PolyLine2Polygon[];
  /**
   * used to create side faces
   */
  edgesToProject: Line2Segment[];
}

function getHoleIntersection(hostFace: Face, hole3: PolyLine3, holeOffset3?: PolyLine3): HoleIntersection {
  const result: HoleIntersection = { intersection: [], edgesToProject: [] };

  if (!hole3?.length) {
    return result;
  }

  const hasOffset = holeOffset3 !== undefined && holeOffset3.length > 0;

  const hostLoop = polyLine2SetCCW(hostFace.loops[0]);
  const holeLoop = polyLine2SetCCW(planeProjectPolyLine3(hostFace.plane, hole3));
  const holeOffset = hasOffset ? planeProjectPolyLine3(hostFace.plane, ensureDefined(holeOffset3)) : undefined;

  switch (classifyContainmentPolyLine2PolyLine2(holeLoop, hostLoop)) {
    case PolyLine2PolyLine2ContainmentClass.INSIDE:
      // fast path:
      // hole is completely inside of host
      if (!!holeOffset) {
        result.intersection.push([polyLine2SetCW(polyLine2Copy(holeLoop)), holeOffset]);
        for (let i = 0; i < holeOffset.length - 2; i += 2) {
          const x1 = holeOffset[i];
          const y1 = holeOffset[i + 1];
          const x2 = holeOffset[i + 2];
          const y2 = holeOffset[i + 3];
          result.edgesToProject.push([x1, y1, x2, y2]);
        }
      } else {
        result.intersection.push([holeLoop]);
        for (let i = 0; i < holeLoop.length - 2; i += 2) {
          const x1 = holeLoop[i];
          const y1 = holeLoop[i + 1];
          const x2 = holeLoop[i + 2];
          const y2 = holeLoop[i + 3];
          result.edgesToProject.push([x1, y1, x2, y2]);
        }
      }
      break;
    case PolyLine2PolyLine2ContainmentClass.OUTSIDE:
      // NOTE: the OUTSIDE case is insufficient to determine if the hole
      // and host are disjoint. Checking the AABBs provides incontrovertible
      // proof.
      if (!aabb2Overlaps(polyLine2AABB2(holeLoop), polyLine2AABB2(hostLoop))) {
        // fast path:
        // hole is completely outside of host
        break;
      }
    // otherwise some part of the hole may overlap the host
    default:
      // slow path:
      // determine which edges of the opening are inside the finish layer
      const holePoly: PolyLine2Polygon = [holeLoop];
      if (holeOffset && holeOffset.length > 0) {
        holePoly.push(holeOffset);
      }

      result.intersection = polygonIntersection({ pls: [holePoly, [hostLoop]] });

      if (result.intersection.length === 0) {
        for (let i = 0; i < hostLoop.length - 2; i += 2) {
          const x1 = hostLoop[i];
          const y1 = hostLoop[i + 1];
          const x2 = hostLoop[i + 2];
          const y2 = hostLoop[i + 3];
          for (const index of polyLine2EdgesCollinearToLine(holeLoop, x1, y1, x2, y2)) {
            result.edgesToProject.push(polyLine2Edge(holeLoop, index) as unknown as Line2Segment);
          }
        }
      }

      for (const poly of result.intersection) {
        for (const pline of poly) {
          for (let i = 0; i < pline.length - 2; i += 2) {
            const x1 = pline[i];
            const y1 = pline[i + 1];
            const x2 = pline[i + 2];
            const y2 = pline[i + 3];

            if (holeOffset3 !== undefined) {
              // ignore edges that are collinear to the outer edge of the hole
              if (polyLine2EdgesCollinearToLine(holeLoop, x1, y1, x2, y2).length === 0) {
                result.edgesToProject.push([x1, y1, x2, y2]);
              }
            } else {
              // ignore edges that are collinear to the clipping line
              if (polyLine2EdgesCollinearToLine(hostLoop, x1, y1, x2, y2).length === 0) {
                result.edgesToProject.push([x2, y2, x1, y1]); // backwards
              }
            }
          }
        }
      }
      break;
  }

  return result;
}

/**
 * extrudes the faces of a hole that are within a host face; edges or sections of edges that lie
 * outside of the host face are discarded
 */
export function extrudeHoleEdges(
  hostFace: Face,
  extrusionDepth: number,
  hole3: PolyLine3 | undefined,
  holeOffset3: PolyLine3 | undefined,
  /**
   * if this hole is part of a larger union, only include edges that are part of the union
   */
  union2?: PolyLine2[],
): Face[] {
  const extruded: Face[] = [];

  if (extrusionDepth === 0 || !hole3?.length) {
    return extruded;
  }

  const [pnx, pny, pnz] = planeNormal(hostFace.plane);
  const [dx, dy, dz] = vec3Scale(-pnx, -pny, -pnz, extrusionDepth);

  const intersection = getHoleIntersection(hostFace, hole3, holeOffset3);

  for (const [x1, y1, x2, y2] of [...intersection.edgesToProject, ...ensureDefined(union2)]) {
    const face = extrudeLine2(hostFace.plane, x1, y1, x2, y2, dx, dy, dz);
    if (face) {
      extruded.push(face);
    }
  }

  for (let p = 0; p < intersection.intersection.length; p++) {
    const intersectionPoly = intersection.intersection[p];
    const backFace = faceReverse(faceNew(intersectionPoly, planeCopyMoved(hostFace.plane, dx, dy, dz)));
    extruded.push(backFace);
  }

  return extruded;
}
